import { memo, useEffect, useState } from 'react'
import { Node, Edge } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/shared/components/ui/select"
import { Badge } from "@/shared/components/ui/badge"
import { Slider } from "@/shared/components/ui/slider"
import { Switch } from "@/shared/components/ui/switch"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { PenLine, Sparkles } from "lucide-react"
import { EnhancedToolSelector } from "@/modules/agent/components/EnhancedToolSelector"
import { UserContextSwitch } from '../UserContextSwitch'
import { SkillApiService } from "@/modules/agent/services/skill-api"
import type { ToolListItem, BuiltinToolInfo } from "@/modules/agent/types/tool"
import type { SkillInfo } from "@/modules/agent/types/skill"

interface ModelOption {
  id: string
  name: string
  description: string
  providerId: string
  providerName: string
  configured?: boolean
}

interface ProviderGroup {
  provider: {
    id: string
    name: string
    configured: boolean
  }
  models: ModelOption[]
}

interface StructuredAgentNodeConfigProps {
  selectedNode: Node
  edges: Edge[]
  outputNodes: Node[]
  projectId: string
  modelsByProvider: ProviderGroup[]
  defaultModel: string
  tools: ToolListItem[]
  builtinTools: BuiltinToolInfo[]
  isLoadingTools: boolean
  userContextApiConfigured: boolean
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
}

export const StructuredAgentNodeConfig = memo(({
  selectedNode,
  projectId,
  modelsByProvider,
  defaultModel,
  tools,
  builtinTools,
  isLoadingTools,
  userContextApiConfigured,
  onUpdateNodeData,
  onOpenPromptEditor
}: StructuredAgentNodeConfigProps) => {

  const [availableSkills, setAvailableSkills] = useState<SkillInfo[]>([])
  const [skillsLoading, setSkillsLoading] = useState(false)
  const [skillsError, setSkillsError] = useState<string | null>(null)

  useEffect(() => {
    if (!projectId) return

    let cancelled = false
    setSkillsLoading(true)
    setSkillsError(null)

    const load = async () => {
      try {
        const [projectRes, libraryRes] = await Promise.all([
          SkillApiService.getProjectSkills(projectId),
          SkillApiService.getSkillLibrary(),
        ])

        if (cancelled) return

        const enabledNames = new Set(
          (projectRes.data?.skills ?? [])
            .filter(s => s.enabled)
            .map(s => s.name)
        )

        const skills = (libraryRes.data?.skills ?? []).filter(s =>
          enabledNames.has(s.name)
        )

        setAvailableSkills(skills)
      } catch (err) {
        if (!cancelled) {
          setSkillsError(err instanceof Error ? err.message : '加载失败')
        }
      } finally {
        if (!cancelled) setSkillsLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [projectId])

  const enabledSkills: string[] = selectedNode.data.enabled_skills ?? []

  const handleSkillToggle = (skillName: string, checked: boolean) => {
    const next = checked
      ? [...enabledSkills, skillName]
      : enabledSkills.filter(s => s !== skillName)
    onUpdateNodeData('enabled_skills', next)
  }

  return (
    <>
      <div className="space-y-2">
        <Label>节点名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
          placeholder="例如：智能助手、任务执行"
        />
      </div>

      {/* 模型选择 */}
      <div className="space-y-2">
        <Label>模型</Label>
        {(() => {
          const currentModel = selectedNode.data.model || defaultModel
          return (
            <Select 
              value={currentModel} 
              onValueChange={(value) => onUpdateNodeData('model', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="选择模型" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {modelsByProvider.length > 0 ? (
                  modelsByProvider.map((group) => (
                    <SelectGroup key={group.provider.id}>
                      <SelectLabel className="flex items-center gap-2">
                        {group.provider.name}
                        {group.provider.configured && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">已配置</Badge>
                        )}
                      </SelectLabel>
                      {group.models.map((model) => (
                        <SelectItem key={model.id} value={model.id} className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            {model.name}
                            {model.id === defaultModel && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700">
                                ⭐ 默认
                              </Badge>
                            )}
                            {model.id === currentModel && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-green-100 text-green-700">
                                ✓ 使用中
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))
                ) : (
                  <SelectItem value="no-models" disabled>
                    暂无可用模型
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )
        })()}
      </div>

      {/* Identity Rules (自定义 System Prompt) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>身份规则 (System Prompt)</Label>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs gap-1"
            onClick={() => onOpenPromptEditor(
              'identity_rules',
              selectedNode.data.identity_rules || '',
              '自定义身份规则',
              '定义 Agent 的身份、规则和行为准则（空则使用全局模板）'
            )}
          >
            <PenLine className="h-3 w-3" />
            编辑
          </Button>
        </div>
        {selectedNode.data.identity_rules && (
          <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border-l-2 border-blue-200 max-h-20 overflow-y-auto">
            {selectedNode.data.identity_rules.slice(0, 100)}
            {selectedNode.data.identity_rules.length > 100 && '...'}
          </div>
        )}
        <p className="text-xs text-slate-500">
          自定义 Agent 的身份、规则和行为准则。留空则使用全局默认模板。
        </p>
      </div>

      {/* 温度控制 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>温度 (Temperature)</Label>
          <span className="text-xs text-slate-500">{(selectedNode.data.temperature ?? 0.7).toFixed(2)}</span>
        </div>
        <Slider
          value={selectedNode.data.temperature ?? 0.7}
          onValueChange={(value) => onUpdateNodeData('temperature', Math.round(value * 100) / 100)}
          max={2}
          min={0}
          step={0.1}
          className="w-full"
        />
        <p className="text-xs text-slate-500">
          控制输出的随机性：0.0=确定性，2.0=高创造性
        </p>
      </div>

      {/* 工具选择 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>工具</Label>
          {(selectedNode.data.tools?.length || 0) > 0 && (
            <Select 
              value={selectedNode.data.tool_call_mode || 'function_calling'} 
              onValueChange={(v) => onUpdateNodeData('tool_call_mode', v)}
            >
              <SelectTrigger className="h-6 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="function_calling">
                  <span className="flex items-center gap-1">
                    Function Calling
                    <span className="text-[9px] text-green-600 font-medium">推荐</span>
                  </span>
                </SelectItem>
                <SelectItem value="json_text">JSON 文本</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <EnhancedToolSelector
          mcpTools={tools}
          builtinTools={builtinTools.filter(t => !['workspace', 'skills', 'code'].includes(t.category))}
          selectedTools={selectedNode.data.tools || []}
          onToolsChange={(newTools) => onUpdateNodeData('tools', newTools)}
          isLoading={isLoadingTools}
          nodeType="structured_agent"
        />
      </div>

      {/* 可用技能 */}
      <div className={`space-y-2 ${selectedNode.data.skills_enabled === false ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <Label>可用技能</Label>
          {enabledSkills.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              已选 {enabledSkills.length}
            </Badge>
          )}
        </div>

        {skillsLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
            加载技能列表...
          </div>
        )}

        {!skillsLoading && skillsError && (
          <div className="text-xs text-red-500 bg-red-50 p-2 rounded">
            {skillsError}
          </div>
        )}

        {!skillsLoading && !skillsError && availableSkills.length === 0 && (
          <p className="text-xs text-slate-400 italic">
            项目暂未启用任何技能，请先在项目设置中配置。
          </p>
        )}

        {!skillsLoading && !skillsError && availableSkills.length > 0 && (
          <div className="space-y-1">
            {availableSkills.map(skill => (
              <div
                key={skill.name}
                className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-slate-50 transition-colors"
              >
                <Checkbox
                  id={`skill-${skill.name}`}
                  checked={enabledSkills.includes(skill.name)}
                  onCheckedChange={(checked) => handleSkillToggle(skill.name, checked as boolean)}
                  className="mt-0.5 shrink-0"
                />
                <label
                  htmlFor={`skill-${skill.name}`}
                  className="flex-1 cursor-pointer space-y-0.5"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono font-medium">{skill.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                      {skill.type}
                    </Badge>
                  </div>
                  {skill.description && (
                    <p className="text-xs text-slate-500 leading-snug">{skill.description}</p>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-400">
          未选择任何技能时，该节点不使用任何技能。
        </p>
      </div>

      {/* Agent Loop 配置 */}
      <div className="pt-4 border-t mt-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium">🔄 Agent Loop</span>
          <Badge variant="secondary" className="text-[10px] bg-teal-100 text-teal-700">核心配置</Badge>
        </div>
        
        <div className="space-y-4">
          {/* 最大步数 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>最大步数</Label>
              <span className="text-xs text-slate-500">{selectedNode.data.max_steps || 10}</span>
            </div>
            <Slider
              value={Number(selectedNode.data.max_steps) || 10}
              onValueChange={(value) => onUpdateNodeData('max_steps', value)}
              max={50}
              min={1}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-slate-500">
              Agent 最多执行多少步骤后强制结束
            </p>
          </div>

          {/* 超时时间 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>超时时间 (秒)</Label>
              <span className="text-xs text-slate-500">{selectedNode.data.timeout_seconds || 120}</span>
            </div>
            <Slider
              value={Number(selectedNode.data.timeout_seconds) || 120}
              onValueChange={(value) => onUpdateNodeData('timeout_seconds', value)}
              max={600}
              min={30}
              step={10}
              className="w-full"
            />
            <p className="text-xs text-slate-500">
              Agent 执行超时后强制结束
            </p>
          </div>
        </div>
      </div>

      {/* Agent 功能开关 */}
      <div className="pt-4 border-t mt-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium">⚙️ 功能开关</span>
        </div>
        
        <div className="space-y-3">
          {/* 安全检查 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="safety-enabled-switch" className="text-sm">
                安全检查
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                启用内容安全过滤和风险检测
              </p>
            </div>
            <Switch
              id="safety-enabled-switch"
              checked={selectedNode.data.safety_enabled ?? true}
              onCheckedChange={(checked) => onUpdateNodeData('safety_enabled', checked)}
            />
          </div>

          {/* 技能调用 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="skills-enabled-switch" className="text-sm">
                技能调用
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                允许调用项目配置的技能
              </p>
            </div>
            <Switch
              id="skills-enabled-switch"
              checked={selectedNode.data.skills_enabled ?? true}
              onCheckedChange={(checked) => onUpdateNodeData('skills_enabled', checked)}
            />
          </div>

          {/* 记忆回忆 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="memory-recall-switch" className="text-sm">
                记忆回忆
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                启用长期记忆存储和检索
              </p>
            </div>
            <Switch
              id="memory-recall-switch"
              checked={selectedNode.data.memory_recall_enabled ?? true}
              onCheckedChange={(checked) => onUpdateNodeData('memory_recall_enabled', checked)}
            />
          </div>

          {/* 代码解释器 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="code-interpreter-switch" className="text-sm">
                代码解释器
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                启用 Python 代码执行环境
              </p>
            </div>
            <Switch
              id="code-interpreter-switch"
              checked={selectedNode.data.enableCodeInterpreter ?? false}
              onCheckedChange={(checked) => onUpdateNodeData('enableCodeInterpreter', checked)}
            />
          </div>

          {/* 消息压缩 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="compaction-switch" className="text-sm">
                消息压缩
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                自动压缩历史消息以节省 Token
              </p>
            </div>
            <Switch
              id="compaction-switch"
              checked={selectedNode.data.compaction_enabled ?? false}
              onCheckedChange={(checked) => onUpdateNodeData('compaction_enabled', checked)}
            />
          </div>

          {/* 消息压缩子配置（始终展示，压缩关闭时禁用） */}
          <div className={`ml-4 pl-3 border-l-2 border-teal-200 space-y-3 transition-opacity ${selectedNode.data.compaction_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
            <div className="space-y-1">
              <Label className="text-xs">最大上下文 Token 数</Label>
              <Input
                type="number"
                value={selectedNode.data.max_context_tokens ?? ''}
                onChange={(e) => onUpdateNodeData('max_context_tokens', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="留空使用默认值"
                className="h-7 text-xs"
                disabled={!selectedNode.data.compaction_enabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">保留最近消息条数</Label>
              <Input
                type="number"
                value={selectedNode.data.keep_recent_messages ?? ''}
                onChange={(e) => onUpdateNodeData('keep_recent_messages', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="留空使用默认值"
                className="h-7 text-xs"
                disabled={!selectedNode.data.compaction_enabled}
              />
            </div>
          </div>

          {/* 显示推理过程 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="show-reasoning-switch" className="text-sm">
                显示推理过程
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                在输出中显示 Agent 的思考过程
              </p>
            </div>
            <Switch
              id="show-reasoning-switch"
              checked={selectedNode.data.show_reasoning ?? true}
              onCheckedChange={(checked) => onUpdateNodeData('show_reasoning', checked)}
            />
          </div>

          {/* 显示工具进度 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="show-tool-progress-switch" className="text-sm">
                显示工具进度
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                实时显示工具调用的执行状态
              </p>
            </div>
            <Switch
              id="show-tool-progress-switch"
              checked={selectedNode.data.show_tool_progress ?? true}
              onCheckedChange={(checked) => onUpdateNodeData('show_tool_progress', checked)}
            />
          </div>

          {/* 静默模式 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="silent-mode-switch" className="text-sm">
                静默模式
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                不向用户显示此节点的输出
              </p>
            </div>
            <Switch
              id="silent-mode-switch"
              checked={selectedNode.data.silent_mode ?? false}
              onCheckedChange={(checked) => onUpdateNodeData('silent_mode', checked)}
            />
          </div>

          {/* 工具调用流式输出 */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="stream-tool-calls-switch" className="text-sm">
                工具调用流式输出
              </Label>
              <p className="text-xs text-slate-500 max-w-[200px]">
                实时流式返回工具调用过程
              </p>
            </div>
            <Switch
              id="stream-tool-calls-switch"
              checked={selectedNode.data.enable_stream_tool_calls ?? false}
              onCheckedChange={(checked) => onUpdateNodeData('enable_stream_tool_calls', checked)}
            />
          </div>
        </div>
      </div>

      {/* 用户上下文开关 */}
      <UserContextSwitch
        selectedNode={selectedNode}
        onUpdateNodeData={onUpdateNodeData}
        userContextApiConfigured={userContextApiConfigured}
        standalone={true}
      />
    </>
  )
})

StructuredAgentNodeConfig.displayName = 'StructuredAgentNodeConfig'